import { useState, useEffect } from "react";
import { fromJS } from "immutable";
import SiteDesignStore from "../stores/SiteDesignStore";
import DesignManager from "../managers/DesignManager";
import SiteService from "../service/SiteService.mjs";

/**
 * Fetches page data for a given page slug.
 * Uses DesignManager cache first, falls back to SiteService.loadPage API.
 * Handles race conditions when pageSlug changes rapidly.
 *
 * @param {string|null} pageSlug - The page slug to fetch
 * @param {Object} options
 * @param {boolean} options.skip - Skip fetching (for conditional fetches)
 * @returns {{ pageData: Immutable.Map|null, isLoading: boolean }}
 */
export const usePage = (pageSlug, { skip = false } = {}) => {
  const [pageData, setPageData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (skip || !pageSlug) {
      setPageData(null);
      setIsLoading(false);
      return;
    }

    const page = SiteDesignStore.getPageList()?.find((p) => p.get("slug") === pageSlug);
    if (!page || !SiteDesignStore.id) {
      setPageData(null);
      setIsLoading(false);
      return;
    }

    const pageId = page.get("id");

    // Check DesignManager first - it has the most up-to-date data for recently edited pages
    const cachedFile = DesignManager.getFile(pageId);
    if (cachedFile?.data) {
      setPageData(cachedFile.data);
      setIsLoading(false);
      return;
    }

    setPageData(null);
    setIsLoading(true);

    SiteService.loadPage({ designId: SiteDesignStore.id, pageId })
      .then((result) => {
        if (cancelled) return;
        const data = fromJS(result.data);
        setPageData(data);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setPageData(null);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pageSlug, skip]);

  return { pageData, isLoading };
};
